import { useState, useEffect, useRef } from 'react';
import { getStoredUTMParameters, storeUTMParameters } from '@/utils/utm';

interface ContactFormData {
  name: string;
  phone: string;
  email: string;
  interest: string;
  startTime: string;
  location: string;
  message: string;
}

interface ContactFormModalProps {
  onSuccess?: () => void;
  onPartialDataChange?: (hasData: boolean) => void;
  onAdditionalFieldsChange?: (showFields: boolean) => void;
  onCompletedAdditionalFieldsChange?: (completed: boolean) => void;
}

export default function ContactFormModal({ onSuccess, onPartialDataChange, onAdditionalFieldsChange, onCompletedAdditionalFieldsChange }: ContactFormModalProps) {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    phone: '',
    email: '',
    interest: '',
    startTime: '',
    location: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showLocationWarning, setShowLocationWarning] = useState(false);
  const [phoneError, setPhoneError] = useState<string>('');
  const hasSubmittedRef = useRef(false);
  const formDataRef = useRef(formData);
  const showAdditionalFieldsRef = useRef(showAdditionalFields);
  const submissionIdRef = useRef<string | null>(null);

  // Update refs when state changes
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    showAdditionalFieldsRef.current = showAdditionalFields;
  }, [showAdditionalFields]);

  // Capture and store UTM parameters on component mount
  useEffect(() => {
    storeUTMParameters();
  }, []);

  // Revelar campos adicionais quando nome, telefone e email estiverem preenchidos
  useEffect(() => {
    // Não executar se formulário foi enviado com sucesso
    if (submitStatus === 'success') {
      return;
    }
    
    const isNameValid = formData.name.trim().length >= 3;
    const phoneValidation = validateWhatsAppNumber(formData.phone);
    const isPhoneValid = phoneValidation.isValid;
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
    
    // Notificar componente pai sobre dados parciais
    if (onPartialDataChange) {
      onPartialDataChange(isNameValid && isPhoneValid && isEmailValid);
    }
    
    if (isNameValid && isPhoneValid && isEmailValid && !showAdditionalFields) {
      setShowAdditionalFields(true);
      if (onAdditionalFieldsChange) {
        onAdditionalFieldsChange(true);
      }
      // Scroll suave para os novos campos após animação
      setTimeout(() => {
        document.getElementById('additional-fields')?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, 400);
    } else if ((!isNameValid || !isPhoneValid || !isEmailValid) && showAdditionalFields) {
      setShowAdditionalFields(false);
      if (onAdditionalFieldsChange) {
        onAdditionalFieldsChange(false);
      }
    }
  }, [formData.name, formData.phone, formData.email, showAdditionalFields, submitStatus, onPartialDataChange, onAdditionalFieldsChange]);

  // Monitorar se campos adicionais foram preenchidos
  useEffect(() => {
    const hasInterest = formData.interest.trim().length > 0;
    const hasStartTime = formData.startTime.trim().length > 0;
    const hasLocation = formData.location.trim().length > 0;
    
    if (onCompletedAdditionalFieldsChange) {
      onCompletedAdditionalFieldsChange(hasInterest && hasStartTime && hasLocation);
    }
  }, [formData.interest, formData.startTime, formData.location, onCompletedAdditionalFieldsChange]);

  // Monitorar seleção de "Outras Zonas"
  useEffect(() => {
    if (formData.location === 'outras-zonas') {
      setShowLocationWarning(true);
    } else {
      setShowLocationWarning(false);
    }
  }, [formData.location]);

  const validateWhatsAppNumber = (phone: string): { isValid: boolean; error: string } => {
    const numbersOnly = phone.replace(/\D/g, '');
    
    // Verificar se tem 11 dígitos (DDD + 9 dígitos)
    if (numbersOnly.length < 11) {
      return { isValid: false, error: 'Número incompleto. Digite DDD + 9 dígitos' };
    }
    
    const ddd = numbersOnly.slice(0, 2);
    const ninthDigit = numbersOnly[2];
    
    // Verificar se DDD é válido (11-99)
    const dddNum = parseInt(ddd);
    if (dddNum < 11 || dddNum > 99) {
      return { isValid: false, error: 'DDD inválido. Use DDD entre 11 e 99' };
    }
    
    // Verificar se o nono dígito é 9 (celular)
    if (ninthDigit !== '9') {
      return { isValid: false, error: 'Número de celular deve começar com 9 após o DDD' };
    }
    
    // Verificar se não é uma sequência repetida (11111111111, 22222222222, etc)
    const allSameDigit = numbersOnly.split('').every(digit => digit === numbersOnly[0]);
    if (allSameDigit) {
      return { isValid: false, error: 'Número inválido. Não use sequências repetidas' };
    }
    
    // Verificar se não é uma sequência óbvia (12345678901, 11111111111, etc)
    const isSequential = numbersOnly === '12345678901' || 
                        numbersOnly === '11111111111' ||
                        numbersOnly === '00000000000';
    if (isSequential) {
      return { isValid: false, error: 'Número inválido. Digite um número real' };
    }
    
    return { isValid: true, error: '' };
  };

  const formatPhone = (value: string): string => {
    // Remove tudo que não é número
    const numbersOnly = value.replace(/\D/g, '');
    
    // Limita a 11 dígitos
    const limited = numbersOnly.slice(0, 11);
    
    // Formata conforme a quantidade de dígitos
    if (limited.length <= 2) {
      return limited;
    } else if (limited.length <= 6) {
      return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
    } else if (limited.length <= 10) {
      return `(${limited.slice(0, 2)}) ${limited.slice(2, 6)}-${limited.slice(6)}`;
    } else {
      return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7, 11)}`;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Se for o campo de telefone, formatar e validar
    if (name === 'phone') {
      const formatted = formatPhone(value);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
      
      // Validar apenas se tiver 11 dígitos
      const numbersOnly = formatted.replace(/\D/g, '');
      if (numbersOnly.length === 11) {
        const validation = validateWhatsAppNumber(formatted);
        setPhoneError(validation.error);
      } else if (numbersOnly.length > 0) {
        setPhoneError('');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const sendFormData = async (statusTag: string) => {
    // Validar todos os campos obrigatórios
    if (!formData.name.trim() || 
        !formData.phone.trim() || 
        !formData.email.trim() || 
        !formData.interest.trim() || 
        !formData.startTime.trim() || 
        !formData.location.trim()) {
      return false;
    }

    const isNameValid = formData.name.trim().length >= 3;
    const phoneValidation = validateWhatsAppNumber(formData.phone);
    const isPhoneValid = phoneValidation.isValid;
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
    const isInterestValid = formData.interest.trim().length > 0;
    const isStartTimeValid = formData.startTime.trim().length > 0;
    const isLocationValid = formData.location.trim().length > 0;
    
    if (!isNameValid || !isPhoneValid || !isEmailValid || !isInterestValid || !isStartTimeValid || !isLocationValid) {
      return false;
    }

    try {

      const conversionDate = new Date().toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      // Get UTM parameters
      const utmParams = getStoredUTMParameters();

      // Gerar ID único para esta submissão
      const currentSubmissionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      submissionIdRef.current = currentSubmissionId;

      const dataToSend = {
        api_key: "582121bf-6661-4c70-81e0-f180f481a92b",
        nome: formData.name.trim(),
        email: formData.email.trim(),
        telefone: `+55${formData.phone.replace(/\D/g, '')}`,
        origem: "landing_page",
        data_conversao: conversionDate,
        status_form: statusTag,
        interesse: formData.interest || 'Não informado',
        quando_pretende_comecar: formData.startTime || 'Não informado',
        local_treinamento: formData.location || 'Não informado',
        mensagem: formData.message.trim() || "Sem mensagem adicional",
        submission_id: currentSubmissionId,
        ...utmParams
      };

      console.log('📤 Enviando dados para M4Track:', dataToSend);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('⏱️ Timeout de 10 segundos atingido');
        controller.abort();
      }, 10000); // 10 segundos é suficiente
      
      try {
        console.log('🌐 Iniciando requisição HTTP para webhook...');
        const response = await fetch('https://webhooks.manager01.m4track.com.br/webhook/site-vox-tatuape', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(dataToSend),
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        
        console.log('✅ M4Track: Resposta recebida - Status:', response.status);
        
        // Se a requisição foi enviada e recebemos uma resposta (mesmo que não seja 2xx), considerar sucesso
        // O webhook do M4Track pode retornar diferentes status codes mas ainda processar o lead
        if (response.status >= 200 && response.status < 500) {
          console.log('✅ M4Track: Formulário enviado com sucesso');
          return true;
        } else {
          console.error('❌ M4Track: Erro no servidor', response.status, response.statusText);
          return false;
        }
      } catch (error: unknown) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
          console.error('❌ Requisição cancelada por timeout');
        } else {
          console.error('❌ Erro na requisição:', error);
          if (error instanceof Error) {
            console.error('❌ Detalhes do erro:', {
              name: error.name,
              message: error.message,
              stack: error.stack
            });
          }
        }
        return false;
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Proteção contra duplo clique
    if (isProcessing || isSubmitting || hasSubmittedRef.current) {
      console.log('⚠️ Envio já em andamento, ignorando clique duplicado');
      return;
    }
    
    setIsProcessing(true);
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Marcar como submetido ANTES de enviar para evitar envio duplicado
    hasSubmittedRef.current = true;

    try {
      const success = await sendFormData('Enviado com sucesso');
      
      if (success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          interest: '',
          startTime: '',
          location: '',
          message: ''
        });
      } else {
        // NÃO resetar hasSubmittedRef para evitar reenvio duplicado
        // Mesmo que falhe, não permitir reenvio automático
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      // NÃO resetar hasSubmittedRef para evitar reenvio duplicado
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setIsProcessing(false);
    }
  };

  // Nota: Envio automático de dados parciais foi removido
  // Agora só envia formulários completos com todos os campos obrigatórios preenchidos

  // Renderizar modal de sucesso se formulário foi enviado
  if (submitStatus === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-4 sm:py-8 px-2 sm:px-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-500 rounded-full flex items-center justify-center mb-3 sm:mb-4 animate-bounce">
              <i className="ri-check-line text-white text-3xl sm:text-4xl"></i>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2 px-2">
              Formulário recebido com sucesso!
            </h3>
            <p className="text-sm sm:text-base text-gray-600 text-center px-2">
              Obrigado por seu interesse na Vox2You Tatuapé - SP!
            </p>
          </div>
          
          <div className="bg-green-50 rounded-xl p-4 sm:p-6 border-2 border-green-200 mb-4 sm:mb-6">
            <div className="flex items-start gap-2 sm:gap-3">
              <i className="ri-whatsapp-fill text-green-600 text-xl sm:text-2xl mt-0.5 flex-shrink-0"></i>
              <div>
                <p className="text-gray-900 font-bold text-base sm:text-lg mb-1 sm:mb-2">
                  Nossa equipe entrará em contato
                </p>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  Em breve você receberá uma mensagem via <strong>WhatsApp</strong> e/ou uma <strong>ligação</strong> de nossa equipe para passar todas as informações sobre nossos programas e tirar suas dúvidas.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={onSuccess}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 text-base sm:text-lg"
          >
            <i className="ri-check-line text-xl sm:text-2xl"></i>
            Entendi, obrigado!
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
            max-height: 0;
          }
          to {
            opacity: 1;
            transform: translateY(0);
            max-height: 2000px;
          }
        }
        
        .animate-slideDown {
          animation: slideDown 0.5s ease-out forwards;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
      
      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        <div>
          <label htmlFor="name" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-2 justify-between">
            <span className="flex items-center gap-1.5 sm:gap-2">
              <i className="ri-user-line text-gray-500 text-sm sm:text-base"></i>
              Nome completo
            </span>
            {formData.name.trim().length >= 3 && (
              <i className="ri-check-line text-green-500 text-lg sm:text-xl"></i>
            )}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            placeholder="Digite seu nome completo"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-2 justify-between">
            <span className="flex items-center gap-1.5 sm:gap-2">
              <i className="ri-whatsapp-line text-gray-500 text-sm sm:text-base"></i>
              WhatsApp
            </span>
            {formData.phone.replace(/\D/g, '').length === 11 && !phoneError && (
              <i className="ri-check-line text-green-500 text-lg sm:text-xl"></i>
            )}
            {phoneError && (
              <i className="ri-error-warning-line text-red-500 text-lg sm:text-xl"></i>
            )}
          </label>
          <div className="flex gap-1.5 sm:gap-2">
            <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg bg-gray-50">
              <span className="text-base sm:text-lg">🇧🇷</span>
              <span className="text-xs sm:text-sm font-medium text-gray-700">+55</span>
            </div>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              placeholder="(11) 99999-9999"
            />
          </div>
          {phoneError ? (
            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
              <i className="ri-error-warning-line"></i>
              {phoneError}
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-1">Digite o DDD + número (máximo 11 dígitos)</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-2 justify-between">
            <span className="flex items-center gap-1.5 sm:gap-2">
              <i className="ri-mail-line text-gray-500 text-sm sm:text-base"></i>
              E-mail *
            </span>
            {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (
              <i className="ri-check-line text-green-500 text-lg sm:text-xl"></i>
            )}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            placeholder="seu@email.com"
          />
          <p className="text-xs text-gray-500 mt-1">Digite um e-mail válido</p>
        </div>

        {!showAdditionalFields && (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
              <i className="ri-arrow-down-line animate-bounce"></i>
              Preencha nome, telefone e e-mail para continuar
            </p>
          </div>
        )}

        {showAdditionalFields && (
          <div id="additional-fields" className="animate-slideDown space-y-3 sm:space-y-4">
            <div className="pt-3 sm:pt-4 border-t border-gray-200">
              <p className="text-xs sm:text-sm text-green-600 font-semibold mb-3 sm:mb-4 flex items-center gap-2">
                <i className="ri-check-double-line"></i>
                Ótimo! Agora mais algumas informações...
              </p>
            </div>

            <div>
        <label htmlFor="interest" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-1.5 sm:gap-2">
          <i className="ri-star-line text-gray-500 text-sm sm:text-base"></i>
          Interesse Principal *
        </label>
        <select
          id="interest"
          name="interest"
          value={formData.interest}
          onChange={handleInputChange}
          required
          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
        >
          <option value="">Selecione uma opção</option>
          <option value="performance-corporativa">Aumentar Performance Corporativa</option>
          <option value="eliminar-medo-tensao">Eliminar Medo/Tensão ao falar em público</option>
          <option value="palestras-apresentacoes">Ministrar Palestras/Apresentações de impacto</option>
          <option value="videos-atrativos">Engajar com vídeos mais atrativos</option>
          <option value="outro-motivo">Outro motivo</option>
        </select>
      </div>

      <div>
        <label htmlFor="startTime" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-1.5 sm:gap-2">
          <i className="ri-calendar-line text-gray-500 text-sm sm:text-base"></i>
          Quando pretende começar? *
        </label>
        <select
          id="startTime"
          name="startTime"
          value={formData.startTime}
          onChange={handleInputChange}
          required
          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
        >
          <option value="">Selecione uma opção</option>
          <option value="urgente">Urgente (o quanto antes)</option>
          <option value="proximo-mes">Para o próximo mês</option>
          <option value="ate-3-meses">Em até 3 meses</option>
          <option value="so-pesquisando">Só pesquisando, meu desenvolvimento não é prioridade</option>
        </select>
      </div>

      <div>
        <label htmlFor="location" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-1.5 sm:gap-2">
          <i className="ri-map-pin-line text-gray-500 text-sm sm:text-base"></i>
          Em qual local de São Paulo você faria o seu treinamento presencial? *
        </label>
        <select
          id="location"
          name="location"
          value={formData.location}
          onChange={handleInputChange}
          required
          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
        >
          <option value="">Selecione uma opção</option>
          <option value="zona-leste">Zona Leste</option>
          <option value="zona-norte">Zona Norte</option>
          <option value="guarulhos">Guarulhos</option>
          <option value="outras-zonas">Outras Zonas</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">Pelo menos 1x por semana com horários flexíveis</p>
        
        {/* Alerta de Outras Zonas */}
        {showLocationWarning && (
          <div className="mt-3 p-3 sm:p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg animate-fadeIn">
            <div className="flex items-start gap-2 sm:gap-3">
              <i className="ri-alert-line text-yellow-600 text-xl sm:text-2xl flex-shrink-0 mt-0.5"></i>
              <div className="flex-1">
                <h4 className="text-sm sm:text-base font-bold text-yellow-800 mb-1">
                  Atenção: Área não atendida
                </h4>
                <p className="text-xs sm:text-sm text-yellow-700 mb-2 sm:mb-3">
                  Esta unidade do Tatuaé atende apenas: <strong>Zona Leste, Zona Norte e Guarulhos</strong>. 
                  Infelizmente não atendemos outras zonas de São Paulo no momento.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, location: '' }));
                    setShowLocationWarning(false);
                  }}
                  className="text-xs sm:text-sm bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg transition-colors duration-200 flex items-center gap-1.5"
                >
                  <i className="ri-arrow-left-line"></i>
                  Escolher outra opção
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div>
        <label htmlFor="message" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-1.5 sm:gap-2">
          <i className="ri-message-3-line text-gray-500 text-sm sm:text-base"></i>
          Mensagem (opcional)
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          rows={3}
          maxLength={500}
          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
          placeholder="Conte-nos mais sobre seus objetivos..."
        ></textarea>
      </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base sm:text-lg shadow-lg hover:shadow-xl"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enviando...
                </>
              ) : (
                <>
                  <i className="ri-whatsapp-line text-xl"></i>
                  Enviar para WhatsApp
                </>
              )}
            </button>

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <i className="ri-error-warning-line text-red-600 text-xl"></i>
                <div>
                  <p className="text-red-800 font-semibold">Erro ao enviar mensagem</p>
                  <p className="text-red-700 text-sm">Por favor, tente novamente.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </form>
    </>
  );
}
